<template>
  <div class="signup-page">
    <v-card class="signup-card" elevation="8">
      <v-card-title class="text-center pl-4 pr-4 pt-3 pb-2 signup-title">
        <img :src="logoImage" height="36" width="auto" class="mb-1 logo-filter">
        <div class="signup-title-text">회원가입</div>
        <div class="signup-subtitle">재가장기요양센터 관리 시스템</div>
      </v-card-title>
      
      <v-card-text>
        <v-form ref="signupForm" @submit.prevent="handleSignup">
          <!-- 기본 정보 섹션 -->
          <div class="form-section">
            <h3 class="section-title">기본 정보</h3>
            
            <div class="compact-field">
              <label class="compact-label">아이디 *</label>
              <div class="address-input-group">
                <input
                  v-model="signupData.m_id"
                  type="text"
                  class="compact-input"
                  placeholder="아이디를 입력하세요"
                  :disabled="loading || checkingId"
                  maxlength="20"
                  autocomplete="username"
                  @input="onIdInput"
                />
                <button
                  type="button"
                  class="address-search-btn id-check-btn"
                  :disabled="!signupData.m_id || signupData.m_id.length < 6 || loading || checkingId"
                  @click="checkIdDuplicate"
                >
                  <v-progress-circular
                    v-if="checkingId"
                    indeterminate
                    size="16"
                    width="2"
                    color="white"
                    class="mr-1"
                  ></v-progress-circular>
                  <span v-else>중복확인</span>
                </button>
              </div>
              <div v-if="idCheckMessage" :class="['id-check-message', idCheckStatus]">
                {{ idCheckMessage }}
              </div>
              <div v-if="!idCheckMessage && signupData.m_id && signupData.m_id.length < 6" class="id-check-message error">
                6자 이상 입력해주세요.
              </div>
            </div>
            
            <div class="compact-field">
              <label class="compact-label">장기요양기관기호 *</label>
              <input
                v-model="signupData.m_longterm_code"
                type="text"
                class="compact-input"
                placeholder="장기요양기관기호를 입력하세요"
                :disabled="loading"
                autocomplete="off"
                maxlength="15"
              />
            </div>
            
            <div class="compact-field">
              <label class="compact-label">비밀번호 *</label>
              <div class="input-with-suffix">
                <input
                  v-model="signupData.m_pw"
                  :type="showPassword ? 'text' : 'password'"
                  class="compact-input"
                  placeholder="비밀번호를 입력하세요"
                  :disabled="loading"
                  autocomplete="new-password"
                  maxlength="50"
                />
                <button
                  type="button"
                  class="password-toggle-btn"
                  @click="showPassword = !showPassword"
                >
                  <v-icon size="16">{{ showPassword ? 'mdi-eye-off' : 'mdi-eye' }}</v-icon>
                </button>
              </div>
            </div>
            
            <div class="compact-field">
              <label class="compact-label">비밀번호 확인 *</label>
              <div class="input-with-suffix">
                <input
                  v-model="signupData.confirmPw"
                  :type="showConfirmPassword ? 'text' : 'password'"
                  class="compact-input"
                  placeholder="비밀번호를 다시 입력하세요"
                  :disabled="loading"
                  autocomplete="new-password"
                  maxlength="50"
                />
                <button
                  type="button"
                  class="password-toggle-btn"
                  @click="showConfirmPassword = !showConfirmPassword"
                >
                  <v-icon size="16">{{ showConfirmPassword ? 'mdi-eye-off' : 'mdi-eye' }}</v-icon>
                </button>
              </div>
              <div v-if="signupData.confirmPw && signupData.m_pw && signupData.confirmPw !== signupData.m_pw" class="id-check-message error">
                비밀번호가 일치하지 않습니다.
              </div>
              <div v-if="signupData.confirmPw && signupData.m_pw && signupData.confirmPw === signupData.m_pw" class="id-check-message success">
                비밀번호가 일치합니다.
              </div>
            </div>
          </div>

          
          <div class="terms-checkbox-wrapper">
            <v-checkbox
              v-model="agreeTerms"
              color="primary"
              :disabled="loading"
              density="compact"
              class="terms-checkbox"
            >
              <template v-slot:label>
                <span class="terms-checkbox-label">
                  <a href="#" @click.prevent="showTermsModal = true" class="terms-link">이용약관</a> 및 
                  <a href="#" @click.prevent="showPrivacyModal = true" class="terms-link">개인정보처리방침</a>에 동의합니다.
                </span>
              </template>
            </v-checkbox>
          </div>
        </v-form>
      </v-card-text>
      
      <v-card-actions class="pl-4 pr-4 pt-2 pb-3">
        <v-btn
          @click="handleSignup"
          color="primary"
          variant="flat"
          block
          size="default"
          :loading="loading"
          :disabled="!isFormValid || isSubmitting"
          rounded="lg"
          class="signup-submit-btn"
        >
          신청하기
        </v-btn>
      </v-card-actions>
      
      <v-divider></v-divider>
      
      <v-card-actions class="pl-4 pr-4 pt-2 pb-3">
        <v-btn
          variant="text"
          block
          size="default"
          :disabled="loading"
          prepend-icon="mdi-login"
          @click="goToLogin"
          class="login-back-btn"
        >
          로그인으로 돌아가기
        </v-btn>
      </v-card-actions>
    </v-card>
    
    <!-- 이용약관 모달 -->
    <v-dialog v-model="showTermsModal" max-width="800" scrollable>
      <v-card rounded="lg">
        <v-card-title class="text-h6 font-weight-bold">이용약관</v-card-title>
        <v-card-text style="height: 500px;">
          <div class="terms-content">
            <h3>제1조 (목적)</h3>
            <p>이 약관은 재가장기요양센터 관리 시스템(이하 "서비스")을 제공하는 하이파이브 주식회사(이하 "회사")와 이를 이용하는 회원(이하 "이용자") 간의 서비스 이용에 관한 제반 사항과 기타 필요한 사항을 구체적으로 규정함을 목적으로 합니다.</p>

            <h3>제2조 (정의)</h3>
            <p>1. "서비스"라 함은 재가장기요양센터의 운영 관리를 위한 웹 기반 시스템을 의미합니다.</p>
            <p>2. "이용자"라 함은 이 약관에 따라 회사가 제공하는 서비스를 받는 회원을 말합니다.</p>
            <p>3. "회원"이라 함은 회사에 개인정보를 제공하여 회원등록을 한 자로서, 회사의 정보를 지속적으로 제공받으며, 회사가 제공하는 서비스를 계속적으로 이용할 수 있는 자를 말합니다.</p>

            <h3>제3조 (약관의 효력 및 변경)</h3>
            <p>1. 이 약관은 서비스를 신청하여 회사의 승낙을 받은 때부터 효력이 발생합니다.</p>
            <p>2. 회사는 약관을 변경하는 경우 변경사유 및 적용일자를 명시하여 현행약관과 함께 서비스 초기화면에 그 적용일자 7일 이전부터 적용일자 전일까지 공지합니다.</p>
            <p>3. 회원이 변경된 약관에 동의하지 않는 경우, 회원은 서비스 이용계약을 해지할 수 있습니다.</p>

            <h3>제4조 (서비스의 제공 및 변경)</h3>
            <p>1. 회사는 다음과 같은 업무를 수행합니다:</p>
            <p>- 재가장기요양센터 운영 관리 시스템 제공</p>
            <p>- 이용자 정보 관리 및 데이터 보안</p>
            <p>- 기타 서비스 관련 필수 업무</p>
            <p>2. 회사는 서비스의 품질 향상을 위해 서비스를 개선할 수 있으며, 중대한 변경사항이 있을 경우 사전에 공지합니다.</p>
            <p>3. 서비스 변경으로 인해 회원에게 불이익이 발생하는 경우, 회사는 합리적인 대안을 제시하거나 보상방안을 마련합니다.</p>

            <h3>제5조 (서비스 이용시간)</h3>
            <p>1. 서비스 이용은 회사의 업무상 또는 기술상 특별한 지장이 없는 한 연중무휴, 1일 24시간 운영을 원칙으로 합니다.</p>
            <p>2. 회사는 시스템 점검, 보수, 교체, 고장, 통신두절 등의 사유로 서비스 제공을 일시 중단할 수 있으며, 예정된 작업의 경우 사전에 공지합니다.</p>
            <p>3. 긴급한 시스템 장애 등 불가피한 사유로 사전 공지가 불가능한 경우, 사후에 즉시 공지하고 조치사항을 안내합니다.</p>

            <h3>제6조 (회원가입)</h3>
            <p>1. 이용자는 회사가 정한 가입 양식에 따라 회원정보를 기입한 후 이 약관에 동의한다는 의사표시를 함으로서 회원가입을 신청합니다.</p>
            <p>2. 회사는 다음 각 호에 해당하지 않는 한 회원가입 신청을 승낙합니다:</p>
            <p>- 가입신청자가 이전에 회원자격을 상실한 적이 있는 경우 (단, 회사의 회원재가입 승낙을 얻은 경우는 예외)</p>
            <p>- 등록 내용에 허위, 기재누락, 오기가 있는 경우</p>
            <p>- 기타 회원으로 등록하는 것이 회사의 기술상 현저히 지장이 있다고 판단되는 경우</p>
            <p>3. 회원가입계약의 성립시기는 회사의 승낙이 회원에게 도달한 시점으로 합니다.</p>

            <h3>제7조 (회원 탈퇴 및 자격 상실)</h3>
            <p>1. 회원은 언제든지 서비스 내 회원탈퇴 메뉴를 통해 이용계약 해지를 요청할 수 있으며, 회사는 관련 법령에 따라 이를 즉시 처리합니다.</p>
            <p>2. 회원이 다음 각 호의 사유에 해당하는 경우, 회사는 사전 통지 후 회원자격을 제한, 정지 또는 상실시킬 수 있습니다:</p>
            <p>- 가입 신청 시 허위 내용을 등록한 경우</p>
            <p>- 다른 사람의 서비스 이용을 방해하거나 그 정보를 도용하는 경우</p>
            <p>- 서비스를 이용하여 법령과 이 약관이 금지하는 행위를 하는 경우</p>
            <p>3. 회사가 회원자격을 제한·정지시킨 후, 동일한 행위가 2회 이상 반복되거나 30일 이내에 그 사유가 시정되지 아니하는 경우 회사는 회원자격을 상실시킬 수 있습니다.</p>
            <p>4. 회원자격 상실 시 회원에게 부가된 혜택은 모두 소멸되며, 이에 대해 회사는 별도로 보상하지 않습니다.</p>

            <h3>제8조 (개인정보보호)</h3>
            <p>회사는 관련법령이 정하는 바에 따라서 회원 등록정보를 포함한 회원의 개인정보를 보호하기 위하여 노력합니다. 회원의 개인정보보호에 관해서는 관련법령 및 회사가 정하는 개인정보처리방침에 정한 바에 의합니다.</p>

            <h3>제9조 (회사의 의무)</h3>
            <p>1. 회사는 법령과 이 약관이 금지하거나 공서양속에 반하는 행위를 하지 않으며, 지속적이고 안정적으로 서비스를 제공하기 위해 최선을 다합니다.</p>
            <p>2. 회사는 회원이 안전하게 서비스를 이용할 수 있도록 개인정보보호를 위한 보안시스템을 구축하고 개인정보처리방침을 공시하고 준수합니다.</p>
            <p>3. 회사는 회원으로부터 제기되는 의견이나 불만이 정당하다고 객관적으로 인정될 경우에는 적절한 절차를 거쳐 즉시 처리하여야 합니다.</p>
            <p>4. 회사는 서비스 이용과 관련하여 회원에게 발생한 손해가 회사의 고의 또는 중과실에 의한 경우 관련 법령에 따라 배상합니다.</p>

            <h3>제10조 (회원의 의무)</h3>
            <p>1. 이용자는 다음 행위를 하여서는 안 됩니다.</p>
            <p>- 신청 또는 변경시 허위 내용의 등록</p>
            <p>- 타인의 정보 도용</p>
            <p>- 회사가 게시한 정보의 변경</p>
            <p>- 회사가 정한 정보 이외의 정보(컴퓨터 프로그램 등) 등의 송신 또는 게시</p>
            <p>- 회사 기타 제3자의 저작권 등 지적재산권에 대한 침해</p>
            <p>- 회사 기타 제3자의 명예를 손상시키거나 업무를 방해하는 행위</p>
            <p>- 외설 또는 폭력적인 메시지, 화상, 음성, 기타 공서양속에 반하는 정보를 회사에 공개 또는 게시하는 행위</p>

            <h3>제11조 (저작권의 귀속 및 이용제한)</h3>
            <p>1. 회사가 작성한 저작물에 대한 저작권 기타 지적재산권은 회사에 귀속합니다.</p>
            <p>2. 회원이 서비스 내에 게시한 게시물에 대한 저작권은 해당 회원에게 귀속됩니다.</p>
            <p>3. 회원은 회사가 제공하는 서비스를 이용하여 얻은 정보 중 지적재산권이 귀속된 정보를 회사의 사전 승낙 없이 복제, 송신, 출판, 배포, 방송 기타 방법에 의하여 영리목적으로 이용하거나 제3자에게 이용하게 하여서는 안 됩니다.</p>
            <p>4. 회사는 회원이 게시하거나 등록하는 서비스 내의 내용물이 다음 각 호에 해당한다고 판단되는 경우에 사전통지 없이 삭제할 수 있습니다:</p>
            <p>- 다른 회원 또는 제3자에게 심한 모독을 주거나 명예를 손상시키는 내용인 경우</p>
            <p>- 공공질서 및 미풍양속에 위반되는 내용을 포함하는 경우</p>
            <p>- 범죄적 행위에 결부된다고 인정되는 내용일 경우</p>
            <p>- 회사의 저작권, 제3자의 저작권 등 기타 권리를 침해하는 내용인 경우</p>

            <h3>제12조 (손해배상 및 면책조항)</h3>
            <p>1. 회사는 무료로 제공되는 서비스와 관련하여 회원에게 어떠한 손해가 발생하더라도 회사가 고의 또는 중과실로 인한 손해발생을 제외하고는 이에 대하여 책임을 지지 않습니다.</p>
            <p>2. 회사는 회원이 서비스에 게재한 정보, 자료, 사실의 신뢰도, 정확성 등의 내용에 관해서는 책임을 지지 않습니다.</p>
            <p>3. 회사는 서비스 이용과 관련하여 가입자에게 발생한 손해 중 가입자의 고의, 과실에 의한 손해에 대하여 책임을 지지 않습니다.</p>

            <h3>제13조 (분쟁해결)</h3>
            <p>1. 회사는 이용자가 제기하는 정당한 의견이나 불만을 반영하고 그 피해를 보상처리하기 위하여 피해보상처리기구를 설치·운영합니다.</p>
            <p>2. 회사와 이용자 간에 발생한 분쟁은 전자상거래 등에서의 소비자보호에 관한 법률에 따른 소비자분쟁조정위원회의 조정에 따를 수 있습니다.</p>
            <p>3. 회사와 이용자 간에 발생한 분쟁에 관한 소송은 제소 당시의 이용자의 주소에 의하고, 주소가 없는 경우에는 거소를 관할하는 지방법원의 전속관할로 합니다. 단, 제소 당시 이용자의 주소 또는 거소가 분명하지 않거나 외국거주자의 경우에는 민사소송법상의 관할법원에 제기합니다.</p>

            <h3>부 칙</h3>
            <p>이 약관은 2025년 1월 1일부터 적용됩니다.</p>
          </div>
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn variant="text" @click="showTermsModal = false">닫기</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 개인정보처리방침 모달 -->
    <v-dialog v-model="showPrivacyModal" max-width="800" scrollable>
      <v-card rounded="lg">
        <v-card-title class="text-h6 font-weight-bold">개인정보처리방침</v-card-title>
        <v-card-text style="height: 500px;">
          <div class="privacy-content">
            <h3>1. 개인정보의 처리목적</h3>
            <p>하이파이브 주식회사(이하 "회사")는 다음의 목적을 위하여 개인정보를 처리하고 있으며, 다음의 목적 이외의 용도로는 이용하지 않습니다.</p>
            <p>- 회원 가입 및 관리</p>
            <p>- 재가장기요양센터 운영 관리 서비스 제공</p>
            <p>- 고객 상담 및 불만 처리</p>
            <p>- 서비스 개선 및 신규 서비스 개발</p>

            <h3>2. 개인정보의 처리 및 보유기간</h3>
            <p>① 회사는 정보주체로부터 개인정보를 수집할 때 동의받은 개인정보 보유·이용기간 또는 법령에 따른 개인정보 보유·이용기간 내에서 개인정보를 처리·보유합니다.</p>
            <p>② 구체적인 개인정보 처리 및 보유 기간은 다음과 같습니다.</p>
            <p>- 회원 가입 및 관리: 회원 탈퇴 시까지</p>
            <p>- 서비스 제공: 서비스 이용계약 종료 시까지</p>
            <p>- 고객 상담: 상담 완료 후 3년</p>

            <h3>3. 개인정보의 제3자 제공</h3>
            <p>① 회사는 정보주체의 개인정보를 제1조(개인정보의 처리목적)에서 명시한 범위 내에서만 처리하며, 정보주체의 동의, 법률의 특별한 규정 등 개인정보보호법 제17조 및 제18조에 해당하는 경우에만 개인정보를 제3자에게 제공합니다.</p>
            <p>② 회사는 다음과 같은 경우에 개인정보를 제3자에게 제공하고 있습니다.</p>
            <p>- 정보주체의 사전 동의를 받은 경우</p>
            <p>- 법률에 특별한 규정이 있거나 법령상 의무를 준수하기 위하여 불가피한 경우</p>

            <h3>4. 개인정보처리 위탁</h3>
            <p>① 회사는 원활한 개인정보 업무처리를 위하여 다음과 같이 개인정보 처리업무를 위탁하고 있습니다.</p>
            <p>- 위탁업체: 클라우드 서비스 제공업체</p>
            <p>- 위탁업무: 시스템 운영 및 데이터 보관</p>
            <p>② 회사는 위탁계약 체결 시 개인정보보호법 제26조에 따라 위탁업무 수행목적 외 개인정보 처리금지, 기술적·관리적 보호조치, 재위탁 제한, 수탁자에 대한 관리·감독, 손해배상 등 책임에 관한 사항을 계약서 등 문서에 명시하고, 수탁자가 개인정보를 안전하게 처리하는지를 감독하고 있습니다.</p>

            <h3>5. 정보주체의 권리·의무 및 그 행사방법</h3>
            <p>① 정보주체는 회사에 대해 언제든지 다음 각 호의 개인정보 보호 관련 권리를 행사할 수 있습니다.</p>
            <p>1. 개인정보 처리현황 통지요구</p>
            <p>2. 개인정보 열람요구</p>
            <p>3. 개인정보 정정·삭제요구</p>
            <p>4. 개인정보 처리정지요구</p>
            <p>② 제1항에 따른 권리 행사는 회사에 대해 개인정보보호법 시행령 제41조제1항에 따라 서면, 전자우편, 모사전송(FAX) 등을 통하여 하실 수 있으며 회사는 이에 대해 지체없이 조치하겠습니다.</p>

            <h3>6. 처리하는 개인정보의 항목</h3>
            <p>회사는 다음의 개인정보 항목을 처리하고 있습니다.</p>
            <p>- 필수항목: 아이디, 비밀번호, 장기요양기관기호</p>

            <h3>7. 개인정보의 파기</h3>
            <p>① 회사는 개인정보 보유기간의 경과, 처리목적 달성 등 개인정보가 불필요하게 되었을 때에는 지체없이 해당 개인정보를 파기합니다.</p>
            <p>② 정보주체로부터 동의받은 개인정보 보유기간이 경과하거나 처리목적이 달성되었음에도 불구하고 다른 법령에 따라 개인정보를 계속 보존하여야 하는 경우에는, 해당 개인정보를 별도의 데이터베이스(DB)로 옮기거나 보관장소를 달리하여 보존합니다.</p>

            <h3>8. 개인정보의 안전성 확보조치</h3>
            <p>회사는 개인정보보호법 제29조에 따라 다음과 같이 안전성 확보에 필요한 기술적/관리적 및 물리적 조치를 하고 있습니다.</p>
            <p>1. 개인정보 취급 직원의 최소화 및 교육</p>
            <p>2. 개인정보에 대한 접근 제한</p>
            <p>3. 개인정보를 안전하게 저장·전송할 수 있는 암호화 기법 사용</p>
            <p>4. 해킹 등에 대비한 기술적 대책</p>
            <p>5. 개인정보처리시스템 등의 접근권한 관리</p>

            <h3>9. 개인정보 보호책임자</h3>
            <p>① 회사는 개인정보 처리에 관한 업무를 총괄해서 책임지고, 개인정보 처리와 관련한 정보주체의 불만처리 및 피해구제 등을 위하여 아래와 같이 개인정보 보호책임자를 지정하고 있습니다.</p>
            <p>▶ 개인정보 보호책임자</p>
            <p>성명: [담당자명]</p>
            <p>직책: [직책]</p>
            <p>연락처: [전화번호], [이메일], [팩스번호]</p>
            <p>② 정보주체께서는 회사의 서비스를 이용하시면서 발생한 모든 개인정보 보호 관련 문의, 불만처리, 피해구제 등에 관한 사항을 개인정보 보호책임자 및 담당부서로 문의하실 수 있습니다.</p>

            <h3>10. 개인정보 처리방침 변경</h3>
            <p>① 이 개인정보처리방침은 시행일로부터 적용되며, 법령 및 방침에 따른 변경내용의 추가, 삭제 및 정정이 있는 경우에는 변경사항의 시행 7일 전부터 공지사항을 통하여 고지할 것입니다.</p>

            <h3>부 칙</h3>
            <p>이 개인정보처리방침은 2025년 1월 1일부터 적용됩니다.</p>
          </div>
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn variant="text" @click="showPrivacyModal = false">닫기</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      timeout="2500"
      rounded="lg"
      location="top"
    >
      {{ snackbar.message }}
    </v-snackbar>
    
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';

interface SignupData {
  m_id: string;
  m_pw: string;
  confirmPw: string;
  m_longterm_code: string;
  m_role: string;
  m_status: string;
  m_isuse: string;
}

const router = useRouter();

// 이미지 경로 설정
const logoImage = ref(new URL('../../../assets/images/hypehive-logo.png', import.meta.url).href);

// 반응형 데이터
const signupData = ref<SignupData>({
  m_id: '',
  m_pw: '',
  confirmPw: '',
  m_longterm_code: '',
  m_role: 'user',
  m_status: 'req',
  m_isuse: 'N',
});

const agreeTerms = ref(false);
const loading = ref(false);
const isSubmitting = ref(false);
const signupForm = ref();
const showPassword = ref(false);
const showConfirmPassword = ref(false);
const snackbar = ref({ show: false, message: '', color: 'info' });
const showTermsModal = ref(false);
const showPrivacyModal = ref(false);
const checkingId = ref(false);
const idCheckMessage = ref('');
const idCheckStatus = ref('');
const isIdAvailable = ref(false);

// 폼 유효성 검사
const isFormValid = computed(() => {
  const d = signupData.value;
  // 아이디 중복 확인, 비밀번호 일치, 약관 동의만 확인
  return isIdAvailable.value && 
         d.m_pw && 
         d.confirmPw && 
         d.m_pw === d.confirmPw && 
         agreeTerms.value;
});

// 회원가입 처리
const handleSignup = async () => {
  if (!isFormValid.value) {
    if (!isIdAvailable.value) {
      snackbar.value.message = '아이디 중복확인이 필요합니다.';
      snackbar.value.color = 'error';
      snackbar.value.show = true;
      return;
    }
    if (!signupData.value.m_pw || !signupData.value.confirmPw) {
      snackbar.value.message = '비밀번호를 입력해주세요.';
      snackbar.value.color = 'error';
      snackbar.value.show = true;
      return;
    }
    if (signupData.value.m_pw !== signupData.value.confirmPw) {
      snackbar.value.message = '비밀번호가 일치하지 않습니다.';
      snackbar.value.color = 'error';
      snackbar.value.show = true;
      return;
    }
    if (!agreeTerms.value) {
      snackbar.value.message = '이용약관에 동의해주세요.';
      snackbar.value.color = 'error';
      snackbar.value.show = true;
      return;
    }
    return;
  }
  
  // 이미 처리 중이면 중복 실행 방지
  if (isSubmitting.value) return;
  
  isSubmitting.value = true;
  loading.value = true;
  
  try {
    // 회원가입 데이터 준비 (confirmPw 제외)
    const { confirmPw, ...signupFormData } = signupData.value;
    
    const response = await fetch('/api/auth/signup', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ form: signupFormData }),
    });
    
    const result = await response.json();
    
    if (result.success) {
      snackbar.value.message = '회원가입이 완료되었습니다.';
      snackbar.value.color = 'success';
      snackbar.value.show = true;
      setTimeout(() => router.push('/pgm/login'), 800);
    } else {
      console.error('회원가입 실패:', result.message);
      snackbar.value.message = result.message || '회원가입에 실패했습니다.';
      snackbar.value.color = 'error';
      snackbar.value.show = true;
    }
  } catch (error) {
    console.error('회원가입 오류:', error);
    snackbar.value.message = '회원가입 중 오류가 발생했습니다.';
    snackbar.value.color = 'error';
    snackbar.value.show = true;
  } finally {
    loading.value = false;
    isSubmitting.value = false;
  }
};

// 로그인 페이지로 이동
const goToLogin = () => {
  router.push('/pgm/login');
};

// 아이디 입력 처리
const onIdInput = () => {
  // 아이디가 변경되면 중복확인 상태 초기화
  idCheckMessage.value = '';
  idCheckStatus.value = '';
  isIdAvailable.value = false;
};

// 아이디 중복확인
const checkIdDuplicate = async () => {
  if (!signupData.value.m_id || signupData.value.m_id.length < 6) {
    idCheckMessage.value = '6자 이상 입력해주세요.';
    idCheckStatus.value = 'error';
    return;
  }

  checkingId.value = true;
  idCheckMessage.value = '';
  idCheckStatus.value = '';

  try {
    const response = await fetch(`/api/auth/memberIdCheck/${signupData.value.m_id}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const result = await response.json();

    if (result.success) {
      // 중복됨
      idCheckMessage.value = '이미 사용 중인 아이디입니다.';
      idCheckStatus.value = 'error';
      isIdAvailable.value = false;
    } else {
      // 사용 가능
      idCheckMessage.value = '사용 가능한 아이디입니다.';
      idCheckStatus.value = 'success';
      isIdAvailable.value = true;
    }
  } catch (error) {
    console.error('아이디 중복확인 오류:', error);
    idCheckMessage.value = '중복확인 중 오류가 발생했습니다.';
    idCheckStatus.value = 'error';
    isIdAvailable.value = false;
  } finally {
    checkingId.value = false;
  }
};
</script>

<style scoped>
.signup-page {
  width: 100%;
  min-height: calc(100vh - 50px);
  background-color: #f7f8fa;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 16px;
  overflow-y: auto;
}

.signup-card {
  width: 100%;
  max-width: 480px;
  min-height: auto;
  border-radius: 10px;
  box-shadow: 0 4px 16px rgba(20, 33, 61, 0.08);
  border: 1px solid #e8eaf0;
  display: flex;
  flex-direction: column;
  background: #ffffff;
}

.signup-card .v-card-title {
  background-color: #ffffff;
  color: #333333;
  border-bottom: 1px solid #e0e0e0;
  border-radius: 8px 8px 0 0;
  flex-shrink: 0;
  padding: 16px 20px 12px 20px !important;
}

.logo-filter {
  filter: none;
}

.v-card-text {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding: 16px 20px;
  gap: 12px;
}


/* 약관/개인정보처리방침 모달 스타일 */
.terms-content,
.privacy-content {
  line-height: 1.6;
}

.terms-content h3,
.privacy-content h3 {
  color: #1976d2;
  font-size: 1.1rem;
  font-weight: 600;
  margin-top: 20px;
  margin-bottom: 8px;
}

.terms-content p,
.privacy-content p {
  margin-bottom: 8px;
  color: #424242;
}

.v-card-actions {
  flex-shrink: 0;
}

.signup-title-text {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 4px;
}

.signup-subtitle {
  font-size: 0.875rem;
  color: #6b7280;
  margin-top: 2px;
}

.signup-submit-btn {
  height: 42px !important;
  font-size: 15px !important;
  font-weight: 600 !important;
  text-transform: none !important;
}

.login-back-btn {
  height: 38px !important;
  font-size: 14px !important;
  text-transform: none !important;
}

/* 약관 동의 체크박스 */
.terms-checkbox-wrapper {
  margin-top: 8px;
  margin-bottom: 4px;
}

.terms-checkbox {
  :deep(.v-selection-control) {
    min-height: 32px;
  }
  
  :deep(.v-label) {
    font-size: 13px;
    line-height: 1.5;
  }
}

.terms-checkbox-label {
  font-size: 13px;
  color: #374151;
}

.terms-link {
  color: #6366f1;
  text-decoration: none;
  font-weight: 600;
  
  &:hover {
    text-decoration: underline;
    color: #4f46e5;
  }
}

/* 섹션 스타일 */
.form-section {
  border: 1px solid #eef1f6;
  border-radius: 8px;
  padding: 12px 14px;
  background-color: #ffffff;
  box-shadow: 0 1px 2px rgba(0,0,0,0.02);
  margin-bottom: 12px;
  
  .section-title {
    color: #133e87;
    font-size: 0.95rem;
    font-weight: 700;
    padding-bottom: 8px;
    margin-bottom: 8px;
    border-bottom: 2px solid #e5e7eb;
  }
  
  .compact-field {
    margin-bottom: 0;
  }
}

.address-input-group {
  display: flex;
  gap: 6px;
  flex: 1;
  align-items: center;
  
  .compact-input {
    flex: 1;
  }
}

/* 반응형 조정 */
@media (max-width: 768px) {
  .signup-page {
    padding: 16px;
  }
  
  .signup-card {
    max-width: 100%;
  }
}

/* 입력 필드 컴팩트 스타일 */
:deep(.v-field) {
  min-height: 44px !important;
}

:deep(.v-field__input) {
  min-height: 44px !important;
  padding-top: 8px !important;
  padding-bottom: 8px !important;
}

:deep(.v-field__outline) {
  --v-field-border-width: 1px;
}

:deep(.v-btn) {
  min-height: 44px !important;
}

/* 푸터 스타일 */
.footer {
  width: 100%;
  max-width: 1000px;
  margin-top: 20px;
  padding: 10px 0;
}

.footer-content {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
}

.footer-text {
  font-size: 0.8rem;
  color: #666;
  font-family: 'Noto Sans KR', sans-serif;
}

.footer-logo {
  opacity: 0.7;
}

/* 폰트 적용 */
.signup-page {
  font-family: 'Noto Sans KR', sans-serif !important;
}

.signup-page * {
  font-family: 'Noto Sans KR', sans-serif !important;
}

/* 아이디 중복확인 스타일 */
.id-check-btn {
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: #9ca3af !important;
    
    &:hover {
      transform: none;
      box-shadow: none;
    }
  }
}

.id-check-message {
  margin-top: 4px;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 4px;
  
  &.success {
    color: #059669;
    background-color: #d1fae5;
  }
  
  &.error {
    color: #dc2626;
    background-color: #fee2e2;
  }
}

.password-toggle-btn {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  transition: color 0.15s ease;
  z-index: 1;
  border-radius: 4px;
  
  &:hover {
    color: #374151;
    background-color: rgba(0, 0, 0, 0.02);
  }
  
  &:active {
    background-color: rgba(0, 0, 0, 0.05);
  }
}

/* compact-field 스타일 (더 컴팩트하고 글자 크기 증가) */
.compact-field {
  padding: 6px 0;
  border-bottom: 1px solid #e5e7eb;
  
  &:last-child {
    border-bottom: none;
  }
  
  .compact-label {
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: #4b5563;
    letter-spacing: 0.1px;
    margin-bottom: 4px;
    text-align: left;
  }
  
  .compact-input {
    width: 100%;
    height: 36px;
    padding: 0 12px;
    font-size: 14px;
    font-weight: 500;
    color: #1a1a1a;
    background-color: #ffffff;
    border: 1.5px solid #d1d5db;
    border-radius: 4px;
    transition: all 0.15s ease;
    box-sizing: border-box;
    
    &:focus {
      outline: none;
      border-color: #6366f1;
      box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.1);
    }
    
    &:hover:not(:focus) {
      border-color: #9ca3af;
    }
    
    &[readonly] {
      background-color: #f9fafb;
      color: #1a1a1a;
      cursor: pointer;
      font-weight: 600;
      
      &:focus {
        border-color: #d1d5db;
        box-shadow: none;
      }
      
      &:hover {
        border-color: #9ca3af;
      }
    }
    
    &::placeholder {
      color: #9ca3af;
      font-weight: 400;
      font-size: 13px;
    }
  }
  
  .input-with-suffix {
    position: relative;
    width: 100%;
    
    .compact-input {
      padding-right: 40px;
      width: 100%;
    }
  }
  
  .address-input-group {
    width: 100%;
    
    .compact-input {
      width: auto;
      flex: 1;
    }
  }
}

.address-search-btn {
  padding: 0 14px;
  height: 36px;
  font-size: 13px;
  font-weight: 600;
  color: #ffffff;
  background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
  flex-shrink: 0;
  
  &:hover {
    background: linear-gradient(135deg, #4f46e5 0%, #4338ca 100%);
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(99, 102, 241, 0.3);
  }
  
  &:active {
    transform: translateY(0);
  }
}
</style>
